/**
 * Nach-Anruf-Karte — die Regeln ohne React.
 *
 * Nach einem geführten Gespräch verwandelt sich das Anruf-Popup in eine Karte,
 * in der Kontakt und Vorgang in einem Rutsch erledigt werden. Die Notiz gehört
 * ins Gespräch (Feld im Anruf-Popup, T-13) und wird beim Auflegen direkt am
 * Anruf gespeichert — sie ist kein Grund mehr für die Karte. Damit sie
 * bei 40 Anrufen am Tag nicht zur Klick-Pflicht wird, erscheint sie nur, wenn
 * noch etwas zu tun ist (PO 28.09.2026):
 *
 *  - kein Kontakt zugeordnet (und die Nummer wurde in dieser Sitzung nicht
 *    schon mit „Später" beantwortet), oder
 *  - kein Vorgang zugeordnet, oder
 *  - aus einem Vorgang heraus gewählt, der Anruf hängt aber (noch) nicht dort
 *    (unter seinen Vorgängen — ein Anruf kann an mehreren hängen).
 *
 * Nie nach einem verpassten oder abgelehnten Anruf — da gibt es nichts
 * nachzubereiten. Interne Gespräche (Durchwahl ↔ Durchwahl) brauchen weder
 * Kontakt noch Vorgang — dort nie.
 */

import type { CallLogDto } from "../../services/telephonyService";
import { hatZuordnung } from "./callZuordnungen";
import { istInterneNummer, sollNachAnrufFragen } from "./telephonyUtils";

/** Aus welchem Vorgang/To-do heraus gewählt wurde (`callNumber(…, { vorgangId })`). */
export interface AnrufKontext {
    vorgangId: number;
    todoId?: number | null;
}

export interface NachAnrufPruefung {
    gesprochen: boolean;
    number: string | null;
    hatKontakt: boolean;
    /** Irgendein Vorgang ODER To-do zugeordnet. */
    hatVorgang: boolean;
    /**
     * ALLE Vorgänge des Anrufs (PO 01.10.2026: ein Anruf kann an mehreren
     * hängen) — für den Abgleich mit dem Vorschlag.
     */
    vorgangIds?: readonly number[];
    /** Aus diesem Vorgang heraus wurde gewählt. */
    vorschlag?: AnrufKontext | null;
    /** Nummern, zu denen in dieser Sitzung „Später" gesagt wurde (nur Kontakt). */
    bereitsGefragt?: ReadonlySet<string>;
}

export function sollNachAnrufKarteZeigen(p: NachAnrufPruefung): boolean {
    if (!p.gesprochen) return false;
    const nummer = p.number?.trim() ?? "";
    if (!nummer || istInterneNummer(nummer)) return false;
    const kontaktFehlt =
        !p.hatKontakt &&
        sollNachAnrufFragen({ number: nummer, gesprochen: true, bereitsGefragt: p.bereitsGefragt });
    const vorschlagOffen = !!p.vorschlag && !(p.vorgangIds ?? []).includes(p.vorschlag.vorgangId);
    return kontaktFehlt || !p.hatVorgang || vorschlagOffen;
}

/** Hat der Anruf schon irgendeinen Vorgang (direkt oder über ein To-do)? */
export function anrufHatVorgang(
    call: Pick<CallLogDto, "vorgangId" | "todoId" | "zuordnungen" | "weitereZuordnungenVerborgen"> | null | undefined,
): boolean {
    return hatZuordnung(call);
}

export interface AnrufLadenDeps {
    /** Antwort des Ingest (`softphone.callLogIdFor`). */
    callLogId: Promise<number | null> | null;
    /** `GET telephony/calls?callId=` — die Zeile samt Kontakt/Vorgang. */
    suchen: (callId: string) => Promise<CallLogDto | null>;
}

/**
 * Die frische Zeile zu einem gerade beendeten Gespräch. Erst auf den Ingest
 * warten (dann gibt es die Zeile sicher, inklusive automatischer
 * Vorgangs-Zuordnung), dann per Gesprächs-ID holen. Wirft nie.
 */
export async function anrufZeileLaden(callId: string, deps: AnrufLadenDeps): Promise<CallLogDto | null> {
    try {
        if (deps.callLogId) await deps.callLogId;
    } catch {
        /* Ingest gescheitert — trotzdem suchen, der CDR kann die Zeile angelegt haben */
    }
    try {
        return await deps.suchen(callId);
    } catch {
        return null;
    }
}

/** Gesprächsdauer als „m:ss" bzw. „h:mm:ss". */
export function gespraechsDauer(sec: number): string {
    const s = Math.max(0, Math.round(sec));
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const r = String(s % 60).padStart(2, "0");
    return h > 0 ? `${h}:${String(m).padStart(2, "0")}:${r}` : `${m}:${r}`;
}
